import React, { useEffect, useMemo, Fragment } from 'react';
import { Clock, Share2, ArrowLeft, ArrowRight, Calendar } from 'lucide-react';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { formatBlogDate, getArticleSeo, BLOG_UI } from '../data/blogArticles';
import { useLanguage } from '../i18n/LanguageContext';
import { useBlog } from '../contexts/BlogContext';
import { AdBanner, DEFAULT_ARTICLE_ADS } from '../components/blog/AdBanner';
import { AdSlot } from '../components/ads/AdSlot';
import { useToast } from '../components/ui/Toast';
interface BlogPostPageProps {
  onNavigate: (page: string) => void;
  articleId: string;
}
export function BlogPostPage({
  onNavigate,
  articleId
}: BlogPostPageProps) {
  const {
    language
  } = useLanguage();
  const {
    success
  } = useToast();
  const {
    allArticles,
    findArticle
  } = useBlog();
  const ui = BLOG_UI[language];
  const article = findArticle(articleId);
  // Pick a consistent ad based on article id (so same article always shows same ad)
  const inlineAd = useMemo(() => {
    if (!article) return DEFAULT_ARTICLE_ADS[0];
    const hash = article.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return DEFAULT_ARTICLE_ADS[hash % DEFAULT_ARTICLE_ADS.length];
  }, [article]);
  const sidebarAd = useMemo(() => {
    if (!article) return DEFAULT_ARTICLE_ADS[1];
    const hash = article.id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return DEFAULT_ARTICLE_ADS[(hash + 1) % DEFAULT_ARTICLE_ADS.length];
  }, [article]);
  // Scroll to top and set SEO title
  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      const seo = getArticleSeo(article, language);
      document.title = seo.title;
    }
  }, [articleId, article, language]);
  if (!article) {
    return <div className="w-full bg-slate-50 min-h-screen flex flex-col items-center justify-center p-4" data-id="element-20907">
        <h1 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-20908">
          {ui.articleNotFound}
        </h1>
        <p className="text-slate-600 mb-8 text-center max-w-md" data-id="element-20909">
          {ui.articleNotFoundHint}
        </p>
        <Button onClick={() => onNavigate('blog')} data-id="element-20910">{ui.backToBlogBtn}</Button>
      </div>;
  }
  // Find related articles
  const relatedArticles = allArticles.filter(a => a.category === article.category && a.id !== article.id).slice(0, 3);
  if (relatedArticles.length < 3) {
    const moreArticles = allArticles.filter(a => a.id !== article.id && !relatedArticles.find(r => r.id === a.id)).slice(0, 3 - relatedArticles.length);
    relatedArticles.push(...moreArticles);
  }
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        text: article.excerpt,
        url: window.location.href
      }).catch(console.error);
    } else {
      navigator.clipboard.writeText(window.location.href);
      success(ui.linkCopied);
    }
  };
  // Insert inline ad after the 2nd section
  const AD_INSERT_AFTER_SECTION = 1; // 0-indexed, so after 2nd section
  return <div className="w-full bg-white pb-24" data-id="element-20911">
      {/* Hero */}
      <div className="pt-8 pb-12 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20912">
        <Breadcrumbs items={[{
        label: 'Astriqo',
        onClick: () => onNavigate('home')
      }, {
        label: ui.title,
        onClick: () => onNavigate('blog')
      }, {
        label: article.title
      }]} className="mb-8" data-id="element-20913" />

        <button onClick={() => onNavigate('blog')} className="flex items-center text-sm font-medium text-slate-500 hover:text-primary mb-8 transition-colors" data-id="element-20914">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-20915" /> {ui.backToBlog}
        </button>

        <Badge variant="neutral" className="mb-6" data-id="element-20916">
          {article.category}
        </Badge>

        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-20917">
          {article.title}
        </h1>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between py-6 border-y border-slate-100 mb-8 gap-4" data-id="element-20918">
          <div className="flex items-center" data-id="element-20919">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold mr-3 shrink-0" data-id="element-20920">
              {article.authorInitials}
            </div>
            <div data-id="element-20921">
              <div className="font-medium text-slate-900" data-id="element-20922">{article.author}</div>
              <div className="text-sm text-slate-500 flex items-center flex-wrap gap-y-1" data-id="element-20923">
                <span className="flex items-center" data-id="element-20924">
                  <Calendar className="w-3.5 h-3.5 mr-1" data-id="element-20925" />
                  {formatBlogDate(article.date, language)}
                </span>
                <span className="mx-2 hidden sm:inline" data-id="element-20926">•</span>
                <span className="flex items-center ml-2 sm:ml-0" data-id="element-20927">
                  <Clock className="w-3.5 h-3.5 mr-1" data-id="element-20928" />
                  {article.readTime} {ui.readTime}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-20929">
            <button onClick={handleShare} className="flex items-center px-4 py-2 text-sm font-medium text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-20930">
              <Share2 className="w-4 h-4 mr-2" data-id="element-20931" /> {ui.share}
            </button>
          </div>
        </div>
      </div>

      {/* Cover Image */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-12" data-id="element-20932">
        <div className={`w-full h-64 md:h-96 ${article.coverColor} rounded-2xl flex items-center justify-center overflow-hidden relative`} data-id="element-20933">
          {article.coverImage ? <img src={article.coverImage} alt={article.title} className="w-full h-full object-cover" data-id="element-20934" /> : <>
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/10 to-transparent" data-id="element-20935"></div>
              <span className="text-5xl md:text-8xl font-black text-slate-900/10 uppercase tracking-widest rotate-[-10deg]" data-id="element-20936">
                {article.category}
              </span>
            </>}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-20937">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-20938">
          {/* Left Sidebar (TOC) - Desktop only */}
          <div className="hidden lg:block lg:col-span-3" data-id="element-20939">
            <div className="sticky top-28" data-id="element-20940">
              <h3 className="font-bold text-slate-900 mb-4 text-sm uppercase tracking-wider" data-id="element-20941">
                {ui.toc}
              </h3>
              <ul className="space-y-3 text-sm text-slate-600" data-id="element-20942">
                {article.content.sections.map(section => <li key={section.id} data-id="element-20943">
                    <a href={`#${section.id}`} className="hover:text-primary transition-colors block leading-snug" data-id="element-20944">
                      {section.heading}
                    </a>
                  </li>)}
              </ul>

              <div className="mt-10 pt-8 border-t border-slate-100" data-id="element-20945">
                <h3 className="font-bold text-slate-900 mb-3 text-sm uppercase tracking-wider" data-id="element-20946">
                  {ui.tags}
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-20947">
                  {article.tags.map(tag => <span key={tag} className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-md" data-id="element-20948">
                      #{tag}
                    </span>)}
                </div>
              </div>

              {/* Ad Banner under tags */}
              <div className="mt-8 pt-8 border-t border-slate-100" data-id="element-20949">
                <AdBanner ad={{
                ...sidebarAd,
                onCtaClick: () => onNavigate(sidebarAd.id === 'natal-promo' ? 'service-natal' : sidebarAd.id === 'consultation-promo' ? 'astrologers' : 'service-solar')
              }} variant="sidebar" data-id="element-20950" />
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-6 prose prose-slate prose-lg max-w-none" data-id="element-20951">
            <p className="lead text-xl text-slate-700 mb-10 font-medium leading-relaxed" data-id="element-20952">
              {article.content.lead}
            </p>

            {article.content.sections.map((section, sectionIndex) => <Fragment key={section.id} data-id="element-20953">
                <div className="mb-12" data-id="element-20954">
                  <h2 id={section.id} className="text-2xl sm:text-3xl font-bold text-slate-900 mb-6 scroll-mt-28" data-id="element-20955">
                    {section.heading}
                  </h2>

                  {section.paragraphs.map((p, i) => <p key={i} className="text-slate-700 leading-relaxed mb-6" data-id="element-20956">
                      {p}
                    </p>)}

                  {section.list && <ul className="space-y-3 mb-8" data-id="element-20957">
                      {section.list.map((item, i) => <li key={i} className="text-slate-700 leading-relaxed" data-id="element-20958">
                          {item}
                        </li>)}
                    </ul>}

                  {/* Table Rendering */}
                  {section.table && <div className="my-8" data-id="element-20959">
                      {/* Desktop Table */}
                      <div className="hidden sm:block overflow-x-auto rounded-xl border border-slate-200" data-id="element-20960">
                        <table className="w-full text-left text-sm m-0" data-id="element-20961">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-20962">
                            <tr data-id="element-20963">
                              {section.table.headers.map((header, i) => <th key={i} className="py-3 px-4 font-semibold text-slate-700 m-0" data-id="element-20964">
                                  {header}
                                </th>)}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-20965">
                            {section.table.rows.map((row, rowIndex) => <tr key={rowIndex} className="hover:bg-slate-50/50 transition-colors" data-id="element-20966">
                                {row.map((cell, cellIndex) => <td key={cellIndex} className="py-3 px-4 text-slate-600 m-0" data-id="element-20967">
                                    {cell}
                                  </td>)}
                              </tr>)}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Stacked Cards for Table */}
                      <div className="sm:hidden space-y-4" data-id="element-20968">
                        {section.table.rows.map((row, rowIndex) => <div key={rowIndex} className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-id="element-20969">
                            {row.map((cell, cellIndex) => <div key={cellIndex} className="flex flex-col" data-id="element-20970">
                                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1" data-id="element-20971">
                                  {section.table!.headers[cellIndex]}
                                </span>
                                <span className="text-sm text-slate-800" data-id="element-20972">
                                  {cell}
                                </span>
                              </div>)}
                          </div>)}
                      </div>
                    </div>}

                  {section.quote && <blockquote className="border-l-4 border-primary bg-indigo-50/50 p-6 sm:p-8 rounded-r-2xl italic text-slate-800 my-10 text-lg sm:text-xl font-medium leading-relaxed" data-id="element-20973">
                      "{section.quote}"
                    </blockquote>}
                </div>

                {/* Inline Ad Banner — after 2nd section */}
                {sectionIndex === AD_INSERT_AFTER_SECTION && article.content.sections.length > 2 && <div className="my-10" data-id="element-20974">
                      <AdSlot slotId="SLOT-21" onNavigate={onNavigate} data-id="element-20975" />
                    </div>}
              </Fragment>)}

            {/* Mobile Tags */}
            <div className="lg:hidden mt-12 pt-8 border-t border-slate-100" data-id="element-20976">
              <div className="flex flex-wrap gap-2" data-id="element-20977">
                {article.tags.map(tag => <span key={tag} className="px-3 py-1.5 bg-slate-100 text-slate-600 text-sm rounded-md font-medium" data-id="element-20978">
                    #{tag}
                  </span>)}
              </div>
            </div>
          </div>

          {/* Right Sidebar (CTA & Related) */}
          <div className="lg:col-span-3" data-id="element-20979">
            <div className="sticky top-28 space-y-8" data-id="element-20980">
              {/* Sidebar Ad Banner */}
              <AdSlot slotId="SLOT-03" onNavigate={onNavigate} data-id="element-20981" />

              {article.relatedService && <Card padding="lg" className="bg-slate-900 text-white border-none text-center shadow-xl" data-id="element-20982">
                  <h3 className="font-bold text-xl mb-4 leading-snug" data-id="element-20983">
                    {article.relatedServiceLabel}
                  </h3>
                  <p className="text-slate-400 text-sm mb-6 leading-relaxed" data-id="element-20984">
                    Получите персональный анализ на основе вашей натальной
                    карты. Точные расчёты и глубокая интерпретация.
                  </p>
                  <Button variant="primary" fullWidth onClick={() => onNavigate(article.relatedService!)} data-id="element-20985">
                    {article.relatedServiceCta || 'Узнать подробнее'}
                  </Button>
                </Card>}

              <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100" data-id="element-20986">
                <h3 className="font-bold text-slate-900 mb-5 uppercase tracking-wider text-sm" data-id="element-20987">
                  {ui.readAlso}
                </h3>
                <div className="space-y-5" data-id="element-20988">
                  {relatedArticles.map(relArticle => <div key={relArticle.id} className="group cursor-pointer border-b border-slate-200 pb-5 last:border-0 last:pb-0" onClick={() => onNavigate(`blog-post/${relArticle.slug}`)} data-id="element-20989">
                      <Badge variant="neutral" className="mb-2 text-[10px] px-2 py-0.5" data-id="element-20990">
                        {relArticle.category}
                      </Badge>
                      <h4 className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors mb-2 line-clamp-3 leading-snug" data-id="element-20991">
                        {relArticle.title}
                      </h4>
                      <div className="flex items-center text-xs text-slate-500" data-id="element-20992">
                        <Calendar className="w-3 h-3 mr-1" data-id="element-20993" />
                        {formatBlogDate(relArticle.date, language)}
                      </div>
                    </div>)}
                </div>

                <Button variant="secondary" fullWidth className="mt-6" onClick={() => onNavigate('blog')} data-id="element-20994">
                  {ui.allArticles} <ArrowRight className="w-4 h-4 ml-2" data-id="element-20995" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}